import ApiService from '@shared/services/api.service';

const FinetuneSuggestionService = {
  /** GET /api/finetuning_suggestions/auto/metadata */
  fetchMetadata() {
    return ApiService.get('/finetuning_suggestions/auto/metadata');
  },

  /**
   * Paginated list of auto-finetune jobs for the authenticated customer.
   * GET /api/finetuning_suggestions/auto/jobs
   * Optional start_date/end_date filter created_at and statuses filter.
   */
  fetchJobs(
    page,
    itemsPerPage,
    search = '',
    fromDate = null,
    toDate = null,
    statuses = []
  ) {
    const params = new URLSearchParams({
      page: String(page),
      items_per_page: String(itemsPerPage),
      q: search
    });
    if (fromDate && toDate) {
      params.set('start_date', fromDate);
      params.set('end_date', toDate);
    }
    (statuses || []).forEach((status) => params.append('statuses[]', status));
    return ApiService.get(`/finetuning_suggestions/auto/jobs?${params.toString()}`);
  },

  /** GET /api/finetuning_suggestions/auto/jobs/{id} */
  fetchJobDetail(id) {
    return ApiService.get(`/finetuning_suggestions/auto/jobs/${encodeURIComponent(id)}`);
  },

  /**
   * Delete a completed/failed auto-finetune job (and its S3 result when present).
   * DELETE /api/finetuning_suggestions/auto/jobs/{id}
   */
  deleteJob(id) {
    return ApiService.delete(`/finetuning_suggestions/auto/jobs/${encodeURIComponent(id)}`);
  },

  /**
   * Batch-delete completed/failed auto-finetune jobs.
   * DELETE /api/finetuning_suggestions/auto/jobs/batch_delete
   * Body: { ids: string[] }
   */
  batchDeleteJobs(ids) {
    return ApiService.delete('/finetuning_suggestions/auto/jobs/batch_delete', {
      data: { ids }
    });
  },

  /**
   * Applies threshold suggestions (per-URL selective apply).
   * POST /api/finetuning_suggestions/auto/apply
   * Body: { mode, urls: [{ url_id, thresholds: { threshold_href: 5, ... } }] }
   */
  applyThresholds(urls, mode = 'set') {
    return ApiService.post('/finetuning_suggestions/auto/apply', { mode, urls });
  },

  /**
   * Applies the same thresholds map to many URLs (domain / tag / url_ids scope).
   * POST /api/finetuning_suggestions/auto/apply
   * Body: { mode, target: { domain?, tag_id?, url_ids? }, thresholds }
   */
  applyThresholdsByTarget(target, thresholds, mode = 'floor') {
    return ApiService.post('/finetuning_suggestions/auto/apply', {
      mode,
      target,
      thresholds
    });
  },

  /**
   * Enqueues an async auto-finetuning suggest job for an inclusive datetime window.
   * POST /api/finetuning_suggestions/auto/suggest
   */
  enqueueSuggest({
    start_datetime,
    end_datetime,
    threshold_alert,
    frequency_threshold,
    contact_group_id
  }) {
    const body = { start_datetime, end_datetime };
    if (threshold_alert != null) body.threshold_alert = threshold_alert;
    if (frequency_threshold != null) body.frequency_threshold = frequency_threshold;
    if (contact_group_id != null) body.contact_group_id = contact_group_id;
    return ApiService.post('/finetuning_suggestions/auto/suggest', body);
  },

  /** GET /api/auto_finetune_schedules */
  fetchSchedules() {
    return ApiService.get('/auto_finetune_schedules');
  },

  /** POST /api/auto_finetune_schedules */
  createSchedule(payload) {
    return ApiService.post('/auto_finetune_schedules', payload);
  },

  /** PUT /api/auto_finetune_schedules/{id} */
  updateSchedule(id, payload) {
    return ApiService.put(`/auto_finetune_schedules/${id}`, payload);
  },

  /** DELETE /api/auto_finetune_schedules/{id} */
  deleteSchedule(id) {
    return ApiService.delete(`/auto_finetune_schedules/${id}`);
  }
};

export default FinetuneSuggestionService;
